"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Coins, Sparkles, Zap, ChartBar as BarChart3, Bot, CircleCheck as CheckCircle2, CircleX as XCircle, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefitsList = [{
  icon: UserCheck,
  title: "Direct Expert Access",
  highlight: "100% Dedicated Attention",
  agencyApproach: "Passed down to junior account managers or rotating interns after contract signing.",
  consultantApproach: "You work 100% directly with me, Yassine. Every strategy, audit, and execution is handled personally by my hands."
}, {
  icon: Coins,
  title: "Cost Efficiency & Zero Overhead",
  highlight: "Maximum ROI",
  agencyApproach: "Heavy retainers funding expensive agency offices, sales reps, and administrative bloat.",
  consultantApproach: "Zero middleman markup or agency fluff. Every pound of your investment goes straight into high-impact SEO work."
}, {
  icon: Sparkles,
  title: "Bespoke Strategy vs. Checklists",
  highlight: "Custom Roadmap",
  agencyApproach: "Generic, one-size-fits-all playbooks and automated monthly reporting templates.",
  consultantApproach: "A custom-engineered SEO strategy designed around your specific business model, margins, and target audience."
}, {
  icon: Zap,
  title: "Agile Speed to Market",
  highlight: "Rapid Execution",
  agencyApproach: "Slow sign-off chains, multi-layered bureaucracy, and weeks wasted for simple technical fixes.",
  consultantApproach: "Fast, direct communication and immediate execution. We pivot and capitalize on search updates quickly."
}, {
  icon: BarChart3,
  title: "Transparent & Honest Results",
  highlight: "Revenue Focused",
  agencyApproach: "Vanity metrics, inflated impression stats, and confusing monthly slide decks.",
  consultantApproach: "Plain-English reporting tied directly to qualified leads, organic growth, and genuine business bottom-line impact."
}, {
  icon: Bot,
  title: "Modern AI Search Integration",
  highlight: "Future-Ready",
  agencyApproach: "Lagging behind Google AI Overviews and slow to adapt to new search technologies.",
  consultantApproach: "Proactive AI Search Optimisation integrated seamlessly into your core technical and content SEO roadmap."
}];
export default function Benefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border overflow-hidden">
      <div className="max-w-7xl mx-auto space-y-16">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="outline" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider border-accent/40 bg-secondary text-secondary-foreground rounded-full">
            The Direct Advantage
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default">
            Why Work Directly With Me <span className="text-primary font-serif">Instead of an Agency?</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            When you hire a big agency, you pay for fancy office space and sales teams—while your actual campaign is handed off to junior staff. Working directly with me means 100% senior-level expertise, faster execution, and honest accountability.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefitsList.map((benefit, i) => {
          const IconComponent = benefit.icon;
          return <motion.div key={benefit.title} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-card border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 flex flex-col justify-between">
                  <div>
                    <CardHeader className="space-y-3 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="size-12 rounded-xl bg-secondary text-accent flex items-center justify-center">
                          <IconComponent className="size-6" />
                        </div>
                        <Badge variant="secondary" className="bg-secondary/80 text-secondary-foreground text-xs font-medium rounded-full">
                          {benefit.highlight}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold text-card-foreground font-default pt-2">
                        {benefit.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-0">
                      <div className="space-y-1.5 p-3 rounded-lg bg-muted/60 border border-border/50 text-xs">
                        <div className="flex items-center gap-1.5 font-semibold text-muted-foreground">
                          <XCircle className="size-4 text-destructive shrink-0" />
                          <span>Typical Agency Model</span>
                        </div>
                        <p className="text-muted-foreground/90 pl-5 leading-relaxed">
                          {benefit.agencyApproach}
                        </p>
                      </div>

                      <div className="space-y-1.5 p-3 rounded-lg bg-secondary/50 border border-accent/20 text-xs">
                        <div className="flex items-center gap-1.5 font-semibold text-primary">
                          <CheckCircle2 className="size-4 text-primary shrink-0" />
                          <span>My Direct Approach</span>
                        </div>
                        <p className="text-foreground/90 font-medium pl-5 leading-relaxed">
                          {benefit.consultantApproach}
                        </p>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <Separator className="bg-border" />

        <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 shadow-sm flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="space-y-2 text-center lg:text-left max-w-2xl">
            <h3 className="text-2xl font-bold text-foreground font-default">
              Ready to bypass agency bloat and get real SEO results?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              Let&apos;s discuss your search growth strategy. You&apos;ll speak directly with me, Yassine, not a sales representative.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full lg:w-auto">
            <Button size="lg" className="w-full sm:w-auto font-semibold px-8 gap-2 rounded-lg" asChild>
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button variant="outline" size="lg" className="w-full sm:w-auto font-medium px-6 rounded-lg" asChild>
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}